'use strict';

// Samsung EX-Link driver notes:
// 
// 1. This has only been tested with a UN55ES7100F (Steve's TV).
// 2. Mira Connect can't tell if the display is present or if the serial cable
//    is disconnected. This is because the display does not respond to any
//    queries when it is powered off.
// 3. On-screen menus are not usable while Mira Connect is controlling the
//    display. This is because we are polling every second, and the display
//    treats EX-Link commands as UI input, thus making the on-screen menus
//    disappear.
// 4. If the display is set to an input that would show a "No Signal" message,
//    then that message will flicker every second. This is also because we are
//    polling via EX-Link. (See above)

var AsciiText = require('./util/AsciiText');
var CmdDevice = require('./util/CmdDevice');
var log = require('../../../log/Log').channel('SamsungExLink');

function SamsungExLink(ipAddress, username, password, onConnectStatus, 
	onControlStatus, config) {
	this.busy = false;
	this.power = false;
	this.volume = 0;
	this.volumeMute = true;
	this.videoInput = null;

	this.pollCycle = 0;
	this.receivedPowerPoll = false;

	CmdDevice.call(this, ipAddress, username, password, onConnectStatus,
		onControlStatus, config);
}

SamsungExLink.prototype = Object.create(CmdDevice.prototype);
SamsungExLink.prototype.constructor = SamsungExLink;

SamsungExLink.prototype.name = 'SamsungExLink';
SamsungExLink.prototype.key = '-L6CeavecqBhBWDVvdu7';
SamsungExLink.prototype.isBinary = true;
SamsungExLink.prototype.isAsync = false;
SamsungExLink.prototype.rejectAsync = false;
SamsungExLink.prototype.syncTimeoutMs = 2000;
SamsungExLink.prototype.sendTerminator = '';
SamsungExLink.prototype.receiveTerminators = [];
SamsungExLink.prototype.unterminatedRegExp = /\x03\x0c\xf1|\x03\x0c\xf5|\x08\xf0.{3}\xf1.{4}/;
SamsungExLink.prototype.pingMs = 3000;
SamsungExLink.prototype.pollIntervalMs = 1000;

SamsungExLink.prototype.pollParams = [ '$powerPoll', '$videoInputPoll', '$volumePoll', '$volumeMutePoll' ];

SamsungExLink.prototype.ports = [{
	port: 4999,
	verifySend: null,
	verifyConnection: null,
	verifyTimeoutMs: 0,
	loginSkip: null,
	passwordPrompt: null,
	passwordPromptTimeoutMs: 0,
	passwordDefault: null,
	passwordSend: null,
	loginVerify: null,
	loginReject: null,
	loginVerifyTimeoutMs: 0
}];

SamsungExLink.prototype.simulationParams = {
	caps: {
		hasPowerControl: true,
		hasRoomVolumeMute: true,
		hasVideoInputs: true,
		hasVideoSwitcher: true,
	},
};

SamsungExLink.prototype.commands = {};

SamsungExLink.prototype.commands.$powerPoll = {};
SamsungExLink.prototype.commands.$powerPoll.get = function() {
	if (this.hasSync('$powerPoll')) return;
	this.sendCommand('$powerPoll', this.makeCommand([ 0xf0, 0x00, 0x00, 0x00 ]));
};
SamsungExLink.prototype.commands.$powerPoll.match = /\x03\x0c\xf1/;

SamsungExLink.prototype.commands.$powerStatus = {};
SamsungExLink.prototype.commands.$powerStatus.isAsync = true;
SamsungExLink.prototype.commands.$powerStatus.match = /\x08\xf0\x00\x00\x00\xf1(.)\x00\x00./;
SamsungExLink.prototype.commands.$powerStatus.process = function(match) {
	var newPower = match[1].charCodeAt(0) !== 0;

	this.receivedPowerPoll = true;

	if (this.power !== newPower) {
		this.power = newPower;
		this.broadcastStatus('power', this.power);
	}
};

SamsungExLink.prototype.commands.$videoInputPoll = {};
SamsungExLink.prototype.commands.$videoInputPoll.get = function() {
	if (this.hasSync('$videoInputPoll')) return;
	this.sendCommand('$videoInputPoll', this.makeCommand([ 0xf0, 0x04, 0x00, 0x00 ]));
};
SamsungExLink.prototype.commands.$videoInputPoll.match = /\x03\x0c\xf1/;

SamsungExLink.prototype.commands.$videoInputStatus = {};
SamsungExLink.prototype.commands.$videoInputStatus.isAsync = true;
SamsungExLink.prototype.commands.$videoInputStatus.match = /\x08\xf0\x04\x00\x00\xf1(.)\x00\x00./;
SamsungExLink.prototype.commands.$videoInputStatus.process = function(match) {
	var foundId = this.getVideoInputFromCode(match[1]);

	if (this.videoInput !== foundId) {
		this.videoInput = foundId;
		this.broadcastStatus('videoInput', this.videoInput);
	}
};

SamsungExLink.prototype.commands.$volumePoll = {};
SamsungExLink.prototype.commands.$volumePoll.get = function() {
	if (this.hasSync('$volumePoll')) return;
	this.sendCommand('$volumePoll', this.makeCommand([ 0xf0, 0x01, 0x00, 0x00 ]));
};
SamsungExLink.prototype.commands.$volumePoll.match = /\x03\x0c\xf1/;

SamsungExLink.prototype.commands.$volumeStatus = {};
SamsungExLink.prototype.commands.$volumeStatus.isAsync = true;
SamsungExLink.prototype.commands.$volumeStatus.match = /\x08\xf0\x01\x00\x00\xf1(.)\x00\x00./;
SamsungExLink.prototype.commands.$volumeStatus.process = function(match) {
	var newVolume = 50 * match[1].charCodeAt(0) / 100;

	if (this.volume !== newVolume) {
		this.volume = newVolume;
		this.broadcastStatus('volume', this.volume);
	}
};

SamsungExLink.prototype.commands.$volumeMutePoll = {};
SamsungExLink.prototype.commands.$volumeMutePoll.get = function() {
	if (this.hasSync('$volumeMutePoll')) return;
	this.sendCommand('$volumeMutePoll', this.makeCommand([ 0xf0, 0x02, 0x00, 0x00 ]));
};
SamsungExLink.prototype.commands.$volumeMutePoll.match = /\x03\x0c\xf1/;

SamsungExLink.prototype.commands.$volumeMuteStatus = {};
SamsungExLink.prototype.commands.$volumeMuteStatus.isAsync = true;
SamsungExLink.prototype.commands.$volumeMuteStatus.match = /\x08\xF0\x02\x00\x00\xf1(.)\x00\x00./;
SamsungExLink.prototype.commands.$volumeMuteStatus.process = function(match) {
	var newVolumeMute = match[1].charCodeAt(0) !== 0;

	if (this.volumeMute !== newVolumeMute) {
		this.volumeMute = newVolumeMute;
		this.broadcastStatus('volumeMute', this.volumeMute);
	}
};

SamsungExLink.prototype.commands.busy = {};
SamsungExLink.prototype.commands.busy.get = function() {
	this.broadcastStatus('busy', this.busy);
};

SamsungExLink.prototype.commands.ping = {};
SamsungExLink.prototype.commands.ping.activeStates = [ 'controlling', 'controlFailed' ];
SamsungExLink.prototype.commands.ping.get = function() {
	this.defer((function() { this.pingReceived = true; }).bind(this), 500);
};
SamsungExLink.prototype.commands.ping.match = /.+/;

SamsungExLink.prototype.commands.power = {};
SamsungExLink.prototype.commands.power.get = function() {
	this.broadcastStatus('power', this.power);
};
SamsungExLink.prototype.commands.power.set = function(value) {
	var busyDelayMs = value ? 12000 : 3000;
	this.removePollCommands();
	this.cmdSocket.removeSync('power');
	this.setBusy(true, 1);
	this.defer((function() { this.setBusy(false, busyDelayMs); }).bind(this), 100);
	this.sendCommand('power', this.makeCommand([ 0x00, 0x00, 0x00, value ? 0x02 : 0x01 ]));
	this.suspend(busyDelayMs);
	this.receivedPowerPoll = true;

	if (value !== this.power) {
		this.power = value;
		this.broadcastStatus('power', this.power);
	}
};
SamsungExLink.prototype.commands.power.match = /\x03\x0c\xf1/;

SamsungExLink.prototype.commands.videoInput = {};
SamsungExLink.prototype.commands.videoInput.get = function() {
	if (!this.config.hasOwnProperty('videoInputs')) {
		log.error(this + ': videoInput: get: no videoInputs in config: ' +
			JSON.stringify(this.config, null, 4));
		return;
	}

	this.broadcastStatus('videoInput', this.videoInput);
};
SamsungExLink.prototype.commands.videoInput.set = function(srcId) {
	if (!this.config.hasOwnProperty('videoInputs')) {
		log.error(this + ': videoInput: set: no videoInputs in config: ' +
			JSON.stringify(this.config, null, 4));
		return;
	}

	if (!this.config.videoInputs.hasOwnProperty(srcId)) {
		log.error(this + ': videoInput: set: srcId = ' + srcId +
			' not present in videoInputs for config = ' + 
			JSON.stringify(this.config, null, 4));
	}
	
	this.removePollCommands();
	this.cmdSocket.removeSync('videoInput');
	this.sendCommand('videoInput', this.makeCommand([ 0x0a, 0x00,
		this.videoInputCodes[srcId].sendCode1, 
		this.videoInputCodes[srcId].sendCode2 ]));

	if (this.videoInput !== srcId) {
		this.videoInput = srcId;
		this.broadcastStatus('videoInput', this.videoInput);
	}
};
SamsungExLink.prototype.commands.videoInput.match = /\x03\x0c\xf1/;

SamsungExLink.prototype.commands.volume = {};
SamsungExLink.prototype.commands.volume.get = function() {
	this.broadcastStatus('volume', this.volume);
};
SamsungExLink.prototype.commands.volume.set = function(value) {
	var scaledValue = 100 * value / 50;

	this.cmdSocket.removeSync('volume');
	this.sendCommand('volume', this.makeCommand([ 0x01, 0x00, 0x00, scaledValue ]));

	if (this.volume !== value) {
		this.volume = value;
		this.broadcastStatus('volume', this.volume);
	}

	var newVolumeMute = value === 0;
	if (newVolumeMute !== this.volumeMute) this.set('volumeMute', newVolumeMute);
};
SamsungExLink.prototype.commands.volume.match = /\x03\x0c\xf1/;

SamsungExLink.prototype.commands.volumeMute = {};
SamsungExLink.prototype.commands.volumeMute.get = function() {
	this.broadcastStatus('volumeMute', this.volumeMute);
};
SamsungExLink.prototype.commands.volumeMute.set = function(value) {
	this.cmdSocket.removeSync('volumeMute');

	if (this.volumeMute !== value) {
		this.volumeMute = value;
		this.broadcastStatus('volumeMute', this.volumeMute);
		// The mute command can only toggle, not set to a specific value.
		this.sendCommand('volumeMute', this.makeCommand([ 0x02, 0x00, 0x00, 0x00 ]));
	}
};
SamsungExLink.prototype.commands.volumeMute.match = /\x03\x0c\xf1/;

SamsungExLink.prototype.getVideoInputFromCode = function(code) {
	code = code.charCodeAt(0);
	
	for (var srcId in this.videoInputCodes) {
		if (!this.videoInputCodes.hasOwnProperty(srcId)) continue;
		if (this.videoInputCodes[srcId].receiveCode === code) return srcId;
	}

	log.warning(this + ': unmatched video input code 0x' + code.toString(16));
	return null;
};

SamsungExLink.prototype.makeCommand = function(payload) {
	var bytes = [ 0x08, 0x22 ].concat(payload);
	var checksum = 0;

	for (var i = 0; i < bytes.length; i++) {
		checksum += bytes[i];
	}

	checksum = -checksum & 0x00ff;
	bytes.push(checksum);

	return AsciiText.decode(bytes);
};

SamsungExLink.prototype.onComputeCaps = function() {
	this.videoInputCodes = {};

	if (this.config && this.config.videoInputs) {
		for (var srcId in this.config.videoInputs) {
			if (!this.config.videoInputs.hasOwnProperty(srcId)) continue;
			var codes = this.config.videoInputs[srcId].code.split(' ');
			this.videoInputCodes[srcId] = {
				sendCode1: parseInt(codes[0], 16),
				sendCode2: parseInt(codes[1], 16),
				receiveCode: parseInt(codes[2], 16),
			};
		}
	}

	var caps = this.getCapsTemplate();
	caps.hasVideoSwitcher = true;
	this.computeCapsFromConfig(caps);
	caps.hasBusy = true;
	caps.hasPowerControl = true;
	caps.hasRoomVolume = true;
	caps.hasRoomVolumeMute = true;
	return caps;
};

SamsungExLink.onControl = function() {
	this.power = false;
	this.volume = 0;
	this.volumeMute = 0;
	this.videoInput = null;

	for (var i = 0; i < this.pollParams.length; i++) {
		this.query(this.pollParams[i]);
	}

	this.finishControlling();
};

SamsungExLink.prototype.onPoll = function() {
	if (this.pollCycle < 0 || this.pollCycle >= this.pollParams.length) {
		log.error(this + ': pollCycle ' + this.pollCycle + ' out of range');
		this.pollCycle = 0;
	}

	if (this.pollCycle === 1 && !this.receivedPowerPoll) {
		this.power = false;
		this.broadcastStatus('power', this.power);
	}

	if (!this.power) {
		this.pollCycle = 0;
	}

	if (this.pollCycle === 0) this.receivedPowerPoll = false;

	this.query(this.pollParams[this.pollCycle]);

	this.pollCycle++;
	if (this.pollCycle >= this.pollParams.length) this.pollCycle = 0;
};

SamsungExLink.prototype.removePollCommands = function() {
	for (var i = 0; i < this.pollParams.length; i++) {
		this.cmdSocket.removeSync(this.pollParams[i]);
	}
};

module.exports = SamsungExLink;